import { useCallback, useEffect, useMemo, useState } from "react";

import { useTimezone } from "@contexts/useTimezone";
import {
    PresenteeismFailureAttemptsService,
    PresenteeismFailureAttemptsServiceModel,
} from "@services/PresenteeismFailureAttemptsService";
import { Description } from "@shared/utils/DescriptionsHelper";
import Utils from "@utils/Utils";
import {
    mapPresenteeismFailureAttemptPresentation,
    PresenteeismFailureListPresentationItem,
} from "./PresenteeismFailureAttemptPresentation";

export type PresenteeismFailureListItem = PresenteeismFailureListPresentationItem;

interface UsePresenteeismFailuresScreenViewModelProps {
    orgID: string;
    service: PresenteeismFailureAttemptsService;
}

export const mapFailureAttempt = (
    row: PresenteeismFailureAttemptsServiceModel.PresenteeismFailureAttempt,
): PresenteeismFailureListItem => mapPresenteeismFailureAttemptPresentation(row);

export const usePresenteeismFailuresScreenViewModel = ({
    orgID,
    service,
}: UsePresenteeismFailuresScreenViewModelProps) => {
    const { timezone } = useTimezone();
    const [rows, setRows] = useState<PresenteeismFailureListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchField, setSearchField] = useState("");
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

    const getDayFromDate = useCallback((date: Date) => {
        return Utils.getDayStringFromTimestamp(date.getTime(), timezone);
    }, [timezone]);

    const day = useMemo(() => getDayFromDate(selectedDate), [getDayFromDate, selectedDate]);

    const fetchRows = useCallback(async () => {
        if (!orgID) {
            setRows([]);
            return;
        }

        const controller = new AbortController();
        setLoading(true);

        try {
            const response = await service.getForDay(orgID, {
                day,
                signal: controller.signal,
            });

            setRows(response.map(mapFailureAttempt).sort((a, b) => b.timestamp - a.timestamp));
        } finally {
            setLoading(false);
        }

        return () => controller.abort();
    }, [day, orgID, service]);

    useEffect(() => {
        void fetchRows();
    }, [fetchRows]);

    const filteredRows = useMemo(() => {
        const normalizedSearch = searchField.toLowerCase().replace(/\s/g, "");

        if (!normalizedSearch) {
            return rows;
        }

        return rows.filter((row) => {
            const searchableText = [
                row.userName,
                row.requesterID,
                row.buildingName,
                row.failureReason,
                row.failureReasonLabel,
                row.errorMessage,
                row.errorMessageLabel,
                row.type,
            ]
                .join(" ")
                .toLowerCase()
                .replace(/\s/g, "");

            return searchableText.includes(normalizedSearch);
        });
    }, [rows, searchField]);

    const formattedDay = useMemo(() => {
        return selectedDate.toLocaleDateString("es-AR", {
            timeZone: timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }, [selectedDate, timezone]);

    return {
        day,
        formattedDay,
        loading,
        rows: filteredRows,
        searchField,
        setSearchField,
        selectedDate,
        setSelectedDate,
        getDayFromDate,
        refresh: fetchRows,
        formatTimestamp: (timestamp: number) => Description.Timestamp.timeAgoWithDate(timestamp, timezone),
    };
};
